// src/components/Sidebar.jsx
import React, { useState } from "react";
import ForumRounded from "@mui/icons-material/ForumRounded";
import WarningAmberRounded from "@mui/icons-material/WarningAmberRounded";
import LogoutRounded from "@mui/icons-material/LogoutRounded";
import Icon from "../shared/components/Icon";
import { Avatar, CategoryIcon } from "../shared/components/Badges";
import usePermissions from "../shared/hooks/usePermissions";
import ChangePasswordModal from "../features/auth/components/ChangePasswordModal";
import '../styles/components/sidebar.css';

export default function Sidebar({
  activeView,
  onView,
  sidebarCategories,
  onCategoryFilter,
  onNotRelated,
  isNotRelated,
  activeCategoryFilter,
  onLogout,
  auth,
}) {
  const { canAccessSettings, canChangePassword, canViewAllTickets } = usePermissions();
  const [showChangePwd, setShowChangePwd] = useState(false);

  function handleLogout() {
    onLogout();
  }
  const displayName = (auth && (auth.user?.full_name || auth.name)) || "Admin";
  const categoryData = sidebarCategories?.items || [];
  const agentInitials = displayName
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  const agentAvatar = { initials: agentInitials, hue: 220 };

  return (
    <aside className="sidebar">
      <div className="brand">
        <div className="brand-mark">
          <ForumRounded sx={{ color: "#fff", fontSize: 22 }} />
        </div>
        <div>
          <div className="brand-name">ISM</div>
          <div className="brand-sub">Service Desk</div>
        </div>
      </div>

      <nav className="nav">
        <div className="nav-section">Workspace</div>
        <button
          className={`nav-item ${activeView === "overview" ? "active" : ""}`}
          onClick={() => {
            onNotRelated(false);
            onCategoryFilter(null);
            onView("overview");
          }}
        >
          <Icon name="chart" size={15} />
          <span>Overview</span>
        </button>
        {canAccessSettings && (
          <button
            className={`nav-item ${activeView === "settings" ? "active" : ""}`}
            onClick={() => onView("settings")}
          >
            <Icon name="settings" size={15} />
            <span>Settings</span>
          </button>
        )}

        <div className="nav-section">Categories</div>
        <button
          className={`nav-item ${!activeCategoryFilter && !isNotRelated ? "active" : ""}`}
          onClick={() => {
            onNotRelated(false);
            onCategoryFilter(null);
            onView("overview");
          }}
        >
          <CategoryIcon
            icon="list"
            color="#6b7280"
            size={18}
            className="nav-cat-icon"
          />
          <span>All</span>
          <span className="nav-count">
            {Number(sidebarCategories?.total) || 0}
          </span>
        </button>
        {(categoryData || [])
          .filter((c) => c.count > 0)
          .map((c) => (
            <button
              key={c.id}
              className={`nav-item ${activeCategoryFilter === c.id ? "active" : ""}`}
              onClick={() => {
                onNotRelated(false);
                onCategoryFilter(c.id);
                onView("overview");
              }}
              title={c.category_name || c.label}
            >
              <CategoryIcon
                icon={c.icon}
                color={c.color}
                size={18}
                className="nav-cat-icon"
              />
              <span>
                {c.category_name || c.label}
              </span>
              <span className="nav-count">{Number(c.count || 0)}</span>
            </button>
          ))}
      </nav>

      {canViewAllTickets && <div className="nav-ai-section">
        <button
          className={`nav-item nav-item--not-related ${isNotRelated ? "active" : ""}`}
          onClick={() => {
            onCategoryFilter(null);
            onNotRelated(true);
            onView("overview");
          }}
          title="Emails filtered by AI as not related to ISM (spam)"
        >
          <span className="nav-item__spam-badge">
            <WarningAmberRounded sx={{ fontSize: 14 }} />
            <span>SPAM / NOT ISM</span>
          </span>
        </button>
      </div>}

      <div className="sidebar-foot">
        <div className="user-card">
          <Avatar agent={agentAvatar} size={32} />
          <div className="user-card__info">
            <div className="user-name">{displayName}</div>
            <div className="user-role">
              {(auth && auth.user?.role) || "ISM"}
            </div>
          </div>
          {canChangePassword && (
            <button
              className="icon-btn"
              aria-label="Change password"
              title="Change password"
              onClick={() => setShowChangePwd(true)}
            >
              <Icon name="key" size={15} />
            </button>
          )}
          <button
            className="icon-btn"
            aria-label="Sign out"
            title="Sign out"
            onClick={handleLogout}
          >
            <LogoutRounded sx={{ fontSize: 15 }} />
          </button>
        </div>
      </div>

      {showChangePwd && (
        <ChangePasswordModal
          onClose={() => setShowChangePwd(false)}
          onLogout={onLogout}
        />
      )}
    </aside>
  );
}
